import Link from "next/link";

/**
 * The three day views — Now, Day map, Calendar — as one switch.
 *
 * They answer three questions about the same day ("what needs me now", "what has
 * actually happened", "what is booked"), so the switch belongs at the top of each
 * of them where a tab switch is expected. It was hand-copied verbatim into
 * today/page.tsx, day/page.tsx and calendar/page.tsx, which is how Calendar ended
 * up shipping without it for a while: a one-way trip you could only leave via the
 * bottom bar. Three copies of a set of tabs is three chances for one to go
 * missing.
 *
 * Day map is deliberately NOT a bottom-bar tab. A reflective surface opened once
 * or twice a day does not earn a permanent slot; a sub-view of Today does.
 */

const VIEWS = [
  { href: "/today", label: "Now" },
  { href: "/day", label: "Day map" },
  { href: "/calendar", label: "Calendar" },
] as const;

export type DayView = (typeof VIEWS)[number]["href"];

export function DayViewsNav({ current }: { current: DayView }) {
  return (
    <nav aria-label="Day views" className="mb-5 flex items-center gap-1.5 text-sm">
      {VIEWS.map((v) =>
        v.href === current ? (
          <span
            key={v.href}
            aria-current="page"
            className="inline-flex min-h-11 items-center rounded-full bg-[var(--accent)] px-4 font-medium text-white"
          >
            {v.label}
          </span>
        ) : (
          // min-h-11: as 32px-tall pills these were three of the 408 controls that
          // missed the 44px touch minimum, sitting side by side at the top of the
          // three most-visited screens.
          <Link
            key={v.href}
            href={v.href}
            className="inline-flex min-h-11 items-center rounded-full border border-[var(--border)] px-4 text-[var(--muted)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
          >
            {v.label}
          </Link>
        ),
      )}
    </nav>
  );
}
